import * as React from "react";
import { Button } from "../../Button";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import { Autocomplete, Option, Dialog, DialogActions, DialogContent, DialogTitleWithCloseButton, TextField } from "../../../../shared/components";

interface RenameDialogProps {
    entityName: string;
    allEntities: Option[];
    availableEntities: Option[];
    selectedValue?: Option;
    acceptCallback: (args: Object) => void;
    closeCallback: () => void;
}

function RenameDialog(props: RenameDialogProps) {
    const { entityName, allEntities, availableEntities, selectedValue: _selectedValue, acceptCallback, closeCallback } = props;

    const schema = z.object({
        entity: z.object({
            value: z.string(),
            label: z.string()
        }, {
            required_error: `Please select a ${entityName} to rename.`
        }),
        name: z.string()
            .trim()
            .min(1, { message: `Please supply a name for the new ${entityName}.` })
    });
    type FormValues = z.infer<typeof schema>;

    const selectedValue = availableEntities.find(e => _.isEqual(e?.value, _selectedValue?.value));
    const { handleSubmit, setError, control } = useForm({ resolver: zodResolver(schema), defaultValues: { entity: selectedValue, name: "" } });

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        if (_.find(allEntities, e => e.label === formModel.name)) {
            setError("name", { message: `'${formModel.name}' is an existing ${entityName} name!  Please choose a different name.`, type: "validation_error" });
            return;
        }

        acceptCallback({
            entity_id: formModel.entity.value,
            entity_label: formModel.entity.label,
            name: formModel.name
        });
    };

    return (
        <Dialog
            open={true}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    closeCallback();
                }
            }}
            disablePortal={true}
            aria-labelledby="form-dialog-title">
            <DialogTitleWithCloseButton onClose={closeCallback} id="form-dialog-title">
                Rename {entityName}
            </DialogTitleWithCloseButton>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <DialogContent>
                    <div className="vertical-form-field">
                        Select {entityName} to rename:
                    </div>
                    <Controller
                        control={control}
                        name="entity"
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <Autocomplete
                                value={value}
                                onChange={(_event, option, _reason) => onChange((option as Option))}
                                error={!!error}
                                helperText={error?.message}
                                isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                getOptionLabel={(option: any) => option?.label || ""}
                                size="small"
                                fullWidth
                                options={availableEntities}
                                disableClearable
                            />
                        )}
                    />

                    <div className="vertical-form-field">
                        Please supply a name for the new {entityName}:
                    </div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="text" color="warning">
                        Rename
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export { RenameDialog };